import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ModalDataService } from '../../../../core/services/modal-data.service';
import { ModalService } from '../../../../core/services/modal.service';
import { UserService } from '../../../../core/services/user.service';
import { SwalService } from '../../../../core/services/swal.service';
import { AngularPhoneNumberInput } from 'angular-phone-number-input';
import { AuthService } from '../../../../core/services/auth.service';
import { VerifyOtpComponent } from '../../../auth/components/verify-otp/verify-otp.component';
import { Subscription } from 'rxjs';

interface UpgradeAccountData {
  account: {
    _id: string;
    firstName: string;
    lastName: string;
    sex: 'male' | 'female';
    birthday: string;
    phoneNumbers: Array<{
      countryCode: string;
      number: string;
      type: string;
      isPrimary: boolean;
    }>;
    accountType: string;
    email?: string;
    passportNumber?: string;
  };
}

@Component({
  selector: 'app-upgrade-account-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AngularPhoneNumberInput,
  ],
  templateUrl: './upgrade-account-modal.component.html',
  styleUrls: ['./upgrade-account-modal.component.scss'],
})
export class UpgradeAccountModalComponent implements OnInit, OnDestroy {
  account: UpgradeAccountData['account'] | null = null;
  loading: boolean = false;
  success: boolean = false;
  phoneNumberData: any = null;
  otpId: string | null = null;
  private subscription = new Subscription();
  private pendingDetachData: any = null;

  upgradeForm = new FormGroup({
    phoneNumber: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.email]), // Email optionnel, seulement validation format si fourni
  });

  preferredCountries: string[] = ['SN', 'GA'];

  constructor(
    private modalService: ModalService,
    private userService: UserService,
    private swalService: SwalService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    // Récupérer les données du compte depuis le service
    const data = ModalDataService.getData();
    if (data && data.account) {
      this.account = data.account;

      // Pré-remplir avec le numéro de téléphone principal existant s'il existe
      if (this.account && this.account.phoneNumbers) {
        const primaryPhone = this.account.phoneNumbers.find(
          (phone) => phone.isPrimary
        );
        if (primaryPhone) {
          const fullPhoneNumber = `${primaryPhone.countryCode}${primaryPhone.number}`;
          this.upgradeForm.patchValue({
            phoneNumber: fullPhoneNumber,
          });
        }
      }
    }

    this.subscription.add(
      this.modalService.getEvents().subscribe((event) => {
        if (event.type === 'otp_verified') {
          const otpData = event.data;
          this.otpId = otpData.otpId;
          if (this.pendingDetachData) {
            const finalDetachData = { ...this.pendingDetachData, otpId: this.otpId };
            this.callDetach(finalDetachData);
          }
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  get modalTitle(): string {
    if (!this.account) return 'Mise à niveau compte';
    return `Mise à niveau compte de ${this.account.firstName} ${this.account.lastName}`;
  }

  onPhoneNumberChange(event: any): void {
    this.phoneNumberData = event;
  }

  onSubmit() {
    if (this.upgradeForm.valid) {
      const formValue = this.upgradeForm.value;
      const phoneValue = formValue.phoneNumber || '';

      if (!phoneValue || (typeof phoneValue === 'string' && !phoneValue.trim())) {
        this.swalService.error('Erreur', 'Le numéro de téléphone est requis');
        return;
      }

      // Email est optionnel, pas de vérification de présence requise

      this.loading = true;

      // Extraire le code pays et le numéro de téléphone
      // Le composant angular-phone-number-input peut retourner un objet ou une chaîne
      let countryCode = '+221'; // Sénégal par défaut
      let phoneNumberOnly = '';

      // Cast vers any pour gérer les différents formats possibles
      const phoneData = phoneValue as any;

      if (typeof phoneData === 'object' && phoneData !== null) {
        // Si c'est un objet avec les propriétés
        if (phoneData.countryCode) {
          countryCode = phoneData.countryCode.startsWith('+') 
            ? phoneData.countryCode 
            : `+${phoneData.countryCode}`;
        }
        if (phoneData.number) {
          phoneNumberOnly = phoneData.number.replace(/[^\d]/g, '');
        } else if (phoneData.internationalNumber) {
          // Extraire le numéro sans l'indicatif
          phoneNumberOnly = phoneData.internationalNumber
            .replace(countryCode, '')
            .replace(/[^\d]/g, '');
        }
      } else {
        // C'est une chaîne, extraire manuellement l'indicatif
        const fullNumber = String(phoneValue);
        
        // Utiliser phoneNumberData si disponible
        if (this.phoneNumberData?.countryCode) {
          countryCode = this.phoneNumberData.countryCode.startsWith('+')
            ? this.phoneNumberData.countryCode
            : `+${this.phoneNumberData.countryCode}`;
        } else {
          // Essayer de détecter l'indicatif au début du numéro
          const knownCodes = ['+221', '+225', '+237', '+241']; // SN, CI, CM, GA
          for (const code of knownCodes) {
            if (fullNumber.startsWith(code)) {
              countryCode = code;
              break;
            }
          }
        }
        
        phoneNumberOnly = fullNumber
          .replace(countryCode, '')
          .replace(/[^\d]/g, '');
      }

      console.log('Extracted phone data:', { countryCode, phoneNumberOnly, originalValue: phoneValue });

      // Préparer les données pour l'appel API
      const detachData: any = {
        countryCode: countryCode,
        phoneNumber: phoneNumberOnly,
        passportNumber: this.account?.passportNumber || '',
      };

      // N'ajouter l'email que s'il est défini et non vide
      if (formValue.email && formValue.email.trim() !== '') {
        detachData.email = formValue.email;
      }

      this.pendingDetachData = detachData;

      const indicative = countryCode.replace('+', '');
      const requestData = {
        phoneNumber: phoneNumberOnly,
        indicative: indicative,
      };

      console.log('Request data:', requestData);

      this.authService.requestOtp(requestData).subscribe({
        next: (response) => {
          this.loading = false;
          const modalId = this.modalService.open({
            component: VerifyOtpComponent,
            data: {
              phoneNumber: requestData.phoneNumber,
              indicative: requestData.indicative,
            },
            title: 'Vérification OTP',
            width: '500px',
          });
        },
        error: (error) => {
          this.loading = false;
          const errorMessage =
            error.error?.message ||
            error.error?.error?.message ||
            "Une erreur est survenue lors de l'envoi du code OTP.";
          this.swalService.error('Erreur', errorMessage);
        },
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  private callDetach(detachData: any) {
    this.loading = true;
    this.userService.detachSubAccount(detachData).subscribe({
      next: (response) => {
        this.loading = false;
        this.success = true;

        this.swalService.success(
          'Succès',
          'Compte mis à niveau avec succès !'
        );

        this.modalService.emitEvent({
          type: 'accountUpgraded',
          data: {
            accountId: this.account?._id,
            phoneNumber: `${detachData.countryCode}${detachData.phoneNumber}`,
            response: response,
          },
        });

        setTimeout(() => {
          this.closeModal();
        }, 2000);
      },
      error: (error) => {
        this.loading = false;
        console.error('Erreur lors de la mise à niveau du compte:', error);

        const errorMessage =
          error.error?.error?.message ||
          error.error?.message ||
          'Erreur lors de la mise à niveau du compte';

        this.swalService.error('Erreur', errorMessage);
      },
    });
  }

  closeModal() {
    this.modalService.closeAll();
  }

  // Marquer tous les champs comme touchés pour afficher les erreurs
  markFormGroupTouched(): void {
    Object.keys(this.upgradeForm.controls).forEach((key) => {
      const control = this.upgradeForm.get(key);
      control?.markAsTouched();
    });
  }

  // Getters pour les erreurs de validation
  get phoneNumberError(): string {
    const control = this.upgradeForm.get('phoneNumber');
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return 'Le numéro de téléphone est requis';
      }
    }
    return '';
  }

  get emailError(): string {
    const control = this.upgradeForm.get('email');
    if (control?.errors && control.touched) {
      if (control.errors['email']) {
        return 'Veuillez entrer un email valide';
      }
    }
    return '';
  }
}
